import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { createSelector } from 'reselect'
import { AdminBillingService } from '../../../openapi'
import { apiCall } from '../../core'

interface UserFeatureUsageInfo {
  isAllowed: boolean
  currentUsage: number
  limit?: number
  resetWindowMinutes?: number
  remaining?: number
  nextResetAt?: string
}

interface CreditBalance {
  subscriptionCredits: number
  topUpCredits: number
  totalCredits: number
}

interface PlanLimits {
  monthlyCredits: number
  maxSeats: number
  maxBrandKits: number
}

interface UserBillingInfo {
  user: {
    id: string
    name: string
    email: string
    role: string
  }
  currentPlan: {
    tier: string
    productName?: string
    isActive: boolean
  }
  businessId?: string
  creditBalance?: CreditBalance
  planLimits?: PlanLimits
  featureUsage: Record<string, UserFeatureUsageInfo>
  webhookPending?: boolean
}

export interface BusinessBillingInfo {
  businessId: string
  entitlement: {
    tier: string
    monthlyCredits: number
    maxSeats: number
    maxBrandKits: number
    productName?: string
    isActive: boolean
  }
  creditBalance: CreditBalance
  transactions: {
    id: string
    type: string
    amount: number
    source: string
    creativeId?: string
    description?: string
    userId?: string
    userName?: string
    createdAt: string
  }[]
  totalTransactions: number
}

export interface UserCreditUsage {
  userId: string
  businessId: string | null
  totalConsumed: number
  bySource: { source: string; count: number; totalCredits: number }[]
  transactions: {
    id: string
    type: string
    amount: number
    source: string
    creativeId?: string
    description?: string
    createdAt: string
  }[]
  totalTransactions: number
}

interface BillingState {
  userBillingInfo: Record<string, UserBillingInfo>
  businessBilling: Record<string, BusinessBillingInfo>
  userCreditUsage: Record<string, UserCreditUsage>
  loading: Record<string, boolean>
  businessLoading: Record<string, boolean>
  creditUsageLoading: Record<string, boolean>
  changingPlan: Record<string, boolean>
  resettingFeature: Record<string, boolean>
  grantingCredits: Record<string, boolean>
  refundingCredits: Record<string, boolean>
  error: string | null
}

const initialState: BillingState = {
  userBillingInfo: {},
  businessBilling: {},
  userCreditUsage: {},
  loading: {},
  businessLoading: {},
  creditUsageLoading: {},
  changingPlan: {},
  resettingFeature: {},
  grantingCredits: {},
  refundingCredits: {},
  error: null,
}

export const fetchUserBillingInfo = createAsyncThunk(
  'billing/fetchUserBillingInfo',
  async (userId: string, { rejectWithValue }) => {
    const result = await apiCall(
      AdminBillingService.adminBillingControllerGetUserBillingInfoV1,
      rejectWithValue,
      userId
    )
    return { userId, billingInfo: result }
  }
)

export const resetUserFeature = createAsyncThunk(
  'billing/resetUserFeature',
  async (params: { userId: string; feature: string }, { rejectWithValue }) => {
    return await apiCall(
      AdminBillingService.adminBillingControllerResetUserFeatureV1,
      rejectWithValue,
      params.userId,
      { feature: params.feature as 'AgentMessageCount' | 'ImageGenerationCount' | 'CreditBalance' }
    )
  }
)

export const changeUserPlan = createAsyncThunk(
  'billing/changeUserPlan',
  async (params: { userId: string; tier: string; duration?: string }, { rejectWithValue }) => {
    const billingInfo = await apiCall(
      AdminBillingService.adminBillingControllerChangeUserPlanV1,
      rejectWithValue,
      params.userId,
      {
        tier: params.tier as 'Free' | 'Pro' | 'Business',
        duration: params.duration as
          | 'daily'
          | 'three_day'
          | 'weekly'
          | 'two_week'
          | 'monthly'
          | 'two_month'
          | 'three_month'
          | 'six_month'
          | 'yearly'
          | 'lifetime'
          | undefined,
      }
    )

    return { userId: params.userId, billingInfo }
  }
)

export const fetchBusinessBilling = createAsyncThunk(
  'billing/fetchBusinessBilling',
  async (
    params: { businessId: string; txLimit?: number; txOffset?: number },
    { rejectWithValue }
  ) => {
    const result = await apiCall(
      AdminBillingService.adminBillingControllerGetBusinessBillingInfoV1,
      rejectWithValue,
      params.businessId,
      params.txLimit ?? 50,
      params.txOffset ?? 0
    )
    return { businessId: params.businessId, billing: result }
  }
)

export const grantBusinessCredits = createAsyncThunk(
  'billing/grantBusinessCredits',
  async (
    params: { businessId: string; amount: number; description?: string },
    { rejectWithValue }
  ) => {
    return await apiCall(
      AdminBillingService.adminBillingControllerGrantCreditsV1,
      rejectWithValue,
      params.businessId,
      { amount: params.amount, description: params.description }
    )
  }
)

export const refundBusinessCredits = createAsyncThunk(
  'billing/refundBusinessCredits',
  async (
    params: { businessId: string; amount: number; description?: string },
    { rejectWithValue }
  ) => {
    return await apiCall(
      AdminBillingService.adminBillingControllerRefundCreditsV1,
      rejectWithValue,
      params.businessId,
      { amount: params.amount, description: params.description }
    )
  }
)

export const changeBusinessPlan = createAsyncThunk(
  'billing/changeBusinessPlan',
  async (
    params: { businessId: string; tier: 'Free' | 'Pro' | 'Business' },
    { rejectWithValue }
  ) => {
    const result = await apiCall(
      AdminBillingService.adminBillingControllerChangeBusinessPlanV1,
      rejectWithValue,
      params.businessId,
      { tier: params.tier }
    )
    return { businessId: params.businessId, billing: result }
  }
)

export const fetchUserCreditUsage = createAsyncThunk(
  'billing/fetchUserCreditUsage',
  async (params: { userId: string; limit?: number; offset?: number }, { rejectWithValue }) => {
    const result = await apiCall(
      AdminBillingService.adminBillingControllerGetUserCreditUsageV1,
      rejectWithValue,
      params.userId,
      params.limit ?? 50,
      params.offset ?? 0
    )
    return { userId: params.userId, usage: result }
  }
)

const billingSlice = createSlice({
  name: 'billing',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchUserBillingInfo.pending, (state, action) => {
        state.loading[action.meta.arg] = true
        state.error = null
      })
      .addCase(fetchUserBillingInfo.fulfilled, (state, action) => {
        const { userId, billingInfo } = action.payload
        state.loading[userId] = false
        if (billingInfo && typeof billingInfo === 'object') {
          state.userBillingInfo[userId] = billingInfo
        }
      })
      .addCase(fetchUserBillingInfo.rejected, (state, action) => {
        const userId = action.meta.arg
        state.loading[userId] = false
        state.error =
          (action.payload as { message?: string } | undefined)?.message ||
          'Failed to fetch billing information'
      })

    builder
      .addCase(resetUserFeature.pending, (state, action) => {
        state.resettingFeature[action.meta.arg.userId] = true
        state.error = null
      })
      .addCase(resetUserFeature.fulfilled, (state, action) => {
        state.resettingFeature[action.meta.arg.userId] = false
      })
      .addCase(resetUserFeature.rejected, (state, action) => {
        state.resettingFeature[action.meta.arg.userId] = false
        state.error =
          (action.payload as { message?: string } | undefined)?.message || 'Failed to reset feature'
      })

    builder
      .addCase(changeUserPlan.pending, (state, action) => {
        state.changingPlan[action.meta.arg.userId] = true
        state.error = null
      })
      .addCase(changeUserPlan.fulfilled, (state, action) => {
        const { userId, billingInfo } = action.payload
        state.changingPlan[userId] = false
        if (billingInfo && typeof billingInfo === 'object') {
          state.userBillingInfo[userId] = billingInfo as UserBillingInfo
        }
      })
      .addCase(changeUserPlan.rejected, (state, action) => {
        state.changingPlan[action.meta.arg.userId] = false
        state.error =
          (action.payload as { message?: string } | undefined)?.message ||
          'Failed to change user plan'
      })

    builder
      .addCase(fetchBusinessBilling.pending, (state, action) => {
        state.businessLoading[action.meta.arg.businessId] = true
        state.error = null
      })
      .addCase(fetchBusinessBilling.fulfilled, (state, action) => {
        const { businessId, billing } = action.payload
        state.businessLoading[businessId] = false
        if (billing && typeof billing === 'object') {
          state.businessBilling[businessId] = billing
        }
      })
      .addCase(fetchBusinessBilling.rejected, (state, action) => {
        state.businessLoading[action.meta.arg.businessId] = false
        state.error =
          (action.payload as { message?: string } | undefined)?.message ||
          'Failed to fetch business billing information'
      })

    builder
      .addCase(grantBusinessCredits.pending, (state, action) => {
        state.grantingCredits[action.meta.arg.businessId] = true
        state.error = null
      })
      .addCase(grantBusinessCredits.fulfilled, (state, action) => {
        state.grantingCredits[action.meta.arg.businessId] = false
      })
      .addCase(grantBusinessCredits.rejected, (state, action) => {
        state.grantingCredits[action.meta.arg.businessId] = false
        state.error =
          (action.payload as { message?: string } | undefined)?.message || 'Failed to grant credits'
      })

    builder
      .addCase(refundBusinessCredits.pending, (state, action) => {
        state.refundingCredits[action.meta.arg.businessId] = true
        state.error = null
      })
      .addCase(refundBusinessCredits.fulfilled, (state, action) => {
        state.refundingCredits[action.meta.arg.businessId] = false
      })
      .addCase(refundBusinessCredits.rejected, (state, action) => {
        state.refundingCredits[action.meta.arg.businessId] = false
        state.error =
          (action.payload as { message?: string } | undefined)?.message ||
          'Failed to refund credits'
      })

    builder
      .addCase(changeBusinessPlan.pending, (state, action) => {
        state.businessLoading[action.meta.arg.businessId] = true
        state.error = null
      })
      .addCase(changeBusinessPlan.fulfilled, (state, action) => {
        const { businessId, billing } = action.payload
        state.businessLoading[businessId] = false
        if (billing && typeof billing === 'object') {
          state.businessBilling[businessId] = billing
        }
      })
      .addCase(changeBusinessPlan.rejected, (state, action) => {
        state.businessLoading[action.meta.arg.businessId] = false
        state.error =
          (action.payload as { message?: string } | undefined)?.message ||
          'Failed to change business plan'
      })

    builder
      .addCase(fetchUserCreditUsage.pending, (state, action) => {
        state.creditUsageLoading[action.meta.arg.userId] = true
        state.error = null
      })
      .addCase(fetchUserCreditUsage.fulfilled, (state, action) => {
        const { userId, usage } = action.payload
        state.creditUsageLoading[userId] = false
        if (usage && typeof usage === 'object') {
          state.userCreditUsage[userId] = usage
        }
      })
      .addCase(fetchUserCreditUsage.rejected, (state, action) => {
        state.creditUsageLoading[action.meta.arg.userId] = false
        state.error =
          (action.payload as { message?: string } | undefined)?.message ||
          'Failed to fetch user credit usage'
      })
  },
})

export const { clearError } = billingSlice.actions
export const billingReducer = billingSlice.reducer

const selectBillingState = (state: { billing: BillingState }) => state.billing
export const selectAllBillingInfo = (state: { billing: BillingState }) =>
  state.billing.userBillingInfo
const selectUserBillingInfoMap = (state: { billing: BillingState }) => state.billing.userBillingInfo
const selectBillingLoadingMap = (state: { billing: BillingState }) => state.billing.loading
const selectChangingPlanMap = (state: { billing: BillingState }) => state.billing.changingPlan
const selectResettingFeatureMap = (state: { billing: BillingState }) =>
  state.billing.resettingFeature
const selectBusinessBillingMap = (state: { billing: BillingState }) => state.billing.businessBilling
const selectBusinessLoadingMap = (state: { billing: BillingState }) => state.billing.businessLoading
const selectGrantingCreditsMap = (state: { billing: BillingState }) => state.billing.grantingCredits
const selectRefundingCreditsMap = (state: { billing: BillingState }) =>
  state.billing.refundingCredits

export const selectUserBillingInfo = (userId: string) =>
  createSelector([selectUserBillingInfoMap], billingInfoMap => billingInfoMap[userId])

export const selectBillingLoading = (userId: string) =>
  createSelector([selectBillingLoadingMap], loadingMap => loadingMap[userId] || false)

export const selectChangingPlan = (userId: string) =>
  createSelector([selectChangingPlanMap], changingPlanMap => changingPlanMap[userId] || false)

export const selectResettingFeature = (userId: string) =>
  createSelector(
    [selectResettingFeatureMap],
    resettingFeatureMap => resettingFeatureMap[userId] || false
  )

export const selectBillingError = createSelector(
  [selectBillingState],
  billingState => billingState.error
)

export const selectBusinessBilling = (businessId: string) =>
  createSelector([selectBusinessBillingMap], businessBillingMap => businessBillingMap[businessId])

export const selectBusinessBillingLoading = (businessId: string) =>
  createSelector(
    [selectBusinessLoadingMap],
    businessLoadingMap => businessLoadingMap[businessId] || false
  )

export const selectGrantingCredits = (businessId: string) =>
  createSelector(
    [selectGrantingCreditsMap],
    grantingCreditsMap => grantingCreditsMap[businessId] || false
  )

export const selectRefundingCredits = (businessId: string) =>
  createSelector(
    [selectRefundingCreditsMap],
    refundingCreditsMap => refundingCreditsMap[businessId] || false
  )

const selectUserCreditUsageMap = (state: { billing: BillingState }) => state.billing.userCreditUsage
const selectCreditUsageLoadingMap = (state: { billing: BillingState }) =>
  state.billing.creditUsageLoading

export const selectUserCreditUsage = (userId: string) =>
  createSelector([selectUserCreditUsageMap], usageMap => usageMap[userId])

export const selectCreditUsageLoading = (userId: string) =>
  createSelector([selectCreditUsageLoadingMap], loadingMap => loadingMap[userId] || false)
